import invariant from "tiny-invariant"

import type * as Types from "../types"

import {
	isNumber,
	isString,
	isDate,
	isDefined,
	isValued,
	isStringOfLength,
	isUndefined,
} from "./typeGuards"

/*****************************
 * Public
 * - Conversion Functions
 ****************************/

/**
 * Ensures that the passed in value is a number. If not it converts it into one.
 *
 * @param value - value to convert
 */
export function forceNumber(value: Types.StringOrNumber): number {
	return isNumber(value) ? value : parseFloat(value)
}

/**
 * Ensures that the passed in value is a number. If not it converts it into one, as long as
 * it is not undefined.
 *
 * @param value - value to convert
 */
export function forceNumberIfExists(
	value: Types.Optional<Types.StringOrNumber>
): Types.Optional<number> {
	return isValued(value) ? forceNumber(value) : undefined
}

/**
 * Ensures that the passed in value is a string. If not it converts it into one.
 *
 * @param value - value to convert
 */
export function forceString(value: Types.PrimitiveValueOrDate): string {
	return isString(value)
		? value
		: isDate(value)
		? value.toJSON()
		: value.toString()
}

/**
 * Ensures that the passed in value is a string. If not it converts it into one, as long as
 * it is not undefined.
 *
 * @param value - value to convert
 */
export function forceStringIfExists(
	value?: Types.Optional<Types.PrimitiveValueOrDate>
): Types.Optional<string> {
	return isValued(value) ? forceString(value) : undefined
}

/**
 * Ensures that the passed in value is a boolean. If not, it converts it into one.
 *
 * @param value - value to convert
 */
export function forceBoolean(value: Types.PrimitiveValue): boolean {
	return isString(value) && value === "false" ? false : Boolean(value)
}

/**
 * Ensures that the passed in value is a boolean. If not it converts it into one, as long as
 * it is not undefined.
 *
 * @param value - value to convert
 * @param defaultValue - return this if value is not defined, defaults to false
 */
export function forceBooleanIfExists(
	value?: Types.Optional<Types.PrimitiveValue>,
	defaultValue: Types.Optional<boolean> = false
): boolean {
	return isDefined(value) ? forceBoolean(value) : defaultValue
}

/**
 * Ensures that the passed in value is a Date. If not, it converts it into one.
 *
 * @param value - value to convert
 */
export function forceDate(value: Types.PossibleDate): Date {
	const firstAttempt = isDate(value) ? value : new Date(value)
	if(!isNaN(firstAttempt.valueOf())) return firstAttempt

	const secondAttempt = 
		isString(value) ? new Date(forceNumber(value)) 
		: undefined

	if(isUndefined(secondAttempt) || isNaN(secondAttempt.valueOf())){
		console.warn(`Could not convert '${value}' to a date. Using default date.`)
		return new Date(0)
	}

	return secondAttempt
}

/**
 * Ensures that the passed in value is a Date. If not it converts it into one, as long as
 * it is not undefined.
 *
 * @param value - value to convert
 */
export function forceDateIfExists(
	value?: Types.Nullable<Types.PossibleDate>
): Types.Optional<Date> {
	return isValued(value) ? forceDate(value) : undefined
}

/*****************************
 * Public
 * - Formatting Functions
 ****************************/

export function asStringOfLength<T extends string, L extends number>(
	value: T,
	length: L
): Types.StringOfLength<T, L> {
	const result = isStringOfLength(value, length)
	invariant(
		result,
		`String '${value}' was expected to be ${length} characters long, was ${value.length}.`
	)
	if (!result) return value.slice(0, length) as Types.StringOfLength<T, L>
	else return value as Types.StringOfLength<T, L>
}

/**
 * Rounds a number to the specified precision.
 *
 * @param value - number to round
 * @param places - decimal places to round to.
 */
export function roundToPrecision(
	value = 0,
	places: Types.Maybe<number> = 2
): number {
	if (places === false) return value
	let step1 = Math.round((value + "e+" + places) as any)
	if (isNaN(step1)) {
		step1 = Math.round(value)
	}
	const step2 = step1 + "e-" + places
	const result = parseFloat(step2)
	return result
}
